import { computed, ref } from "vue";
import {
  type RouteLocationNormalizedGeneric,
  type RouteRecordName,
} from "vue-router";
import { storeToRefs } from "pinia";
import { AppIdentifier } from "@/shared/@types/AppIdentifier";
import { AppRouteNames } from "@/shared/@types/AppRouteNames";
import { Roles } from "@/shared/@types/Roles";
import { RoleActions } from "@/shared/@types/RoleActions";
import { useAccountStore } from "@/shared/stores/AccountStore";
import { useSelectedCustomerStore } from "@/shared/stores/SelectedCustomerStore";
import { usePermission } from "@/shared/composables/usePermission.composable";
import { useOciSession } from "@/shared/auth/useOciSession.composable";
import { useLogout } from "@/shared/auth/useLogout.composable";
import { isNotProduction } from "@/shared/helper/environment.helper";
import { getCustomerIdsWithAppPermission } from "@/shared/helper/getCustomerIdsWithAppPermission.helper";

export const useAuthenticatedUsersOnly = (
  to: RouteLocationNormalizedGeneric,
) => {
  const accountStore = useAccountStore();
  const { user, allCustomers, fetchingUserError, sapCustomerExists } =
    storeToRefs(accountStore);
  const selectedCustomerStore = useSelectedCustomerStore();
  const {
    selectedCustomerId,
    selectedCustomer,
    customerRoles,
    webStores,
    defaultWebStores,
    globalGasWebStores,
  } = storeToRefs(selectedCustomerStore);
  const { hasPermission } = usePermission();
  const { isOciCustomerWithoutSession } = useOciSession();
  const { logout } = useLogout();

  // ----- Case: User data is not available ---------------------------------------------------
  const hasUserData = computed(() => !!user.value);
  const hasFetchingUserError = computed(() => !!fetchingUserError.value);

  // If no user is available fetch it and set a initial customer directly
  const initializeUserData = async (appToVisit: AppIdentifier) => {
    await accountStore.fetchUser();

    if (!hasFetchingUserError.value) {
      selectedCustomerStore.setInitialSelectedCustomerId(appToVisit);

      // OCI-users without a valid session should not be able to access the app.
      if (isOciCustomerWithoutSession.value) {
        logout();
      }

      /* The web stores from selected customer determine which gas shops (i.e. service cards on the dashboard) are available for the selected account,
       * as an account might have the "gas shop" service assignment and role but should have access to multiple stores of different
       * countries. So we need to fetch and store them here. */
      if (webStores.value.length === 0) {
        await selectedCustomerStore.fetchSelectedCustomer();
      }
    }
  };

  // ----- Case: User has not completed registration for Fleet at Home ------------------------
  // User needs to complete registration if address or bank details are missing and the user has access the Fleet at Home app
  const routesAccessibleWithoutCustomer: RouteRecordName[] = [
    AppRouteNames.FinishRegistration,
    AppRouteNames.ThgQuoteNotFound,
    AppRouteNames.ThgQuoteOverview,
    AppRouteNames.ThgQuoteUnauthenticatedVehicleRegistration,
    AppRouteNames.ThgQuoteVehicleRegistration,
  ];

  const accessRouteWithCustomerRequired = ref(
    !routesAccessibleWithoutCustomer.includes(to.name),
  );

  const needsToCompleteRegistration = computed(
    () =>
      !sapCustomerExists.value &&
      customerRoles.value.includes(Roles.FLEET_AT_HOME_USER),
  );

  // ----- Case: User has no access to the app with the selected customer ----------------------
  const hasOtherCustomerWithPermission = (appToVisit: AppIdentifier) => {
    if (!user.value) return false;

    return getCustomerIdsWithAppPermission(
      user.value.permissions,
      allCustomers.value,
      appToVisit,
    ).some((customerId) => customerId !== selectedCustomerId.value);
  };

  // ----- Case: User comes from wid-services and selected-customer has no access to the app ---
  const selectCustomerWithPermission = async (appToVisit: AppIdentifier) => {
    if (!user.value) return false;

    const customerId = getCustomerIdsWithAppPermission(
      user.value.permissions,
      allCustomers.value,
      appToVisit,
    )[0];
    if (!customerId) return false;

    selectedCustomerStore.setSelectedCustomerId(customerId);
    await selectedCustomerStore.fetchSelectedCustomer();
    return true;
  };

  // ----- Case: User tries to access Gas Shop app ---------------------------------------------
  const hasPermissionForWebStore = (webStoreIdParam: string) => {
    if (
      defaultWebStores.value.some((webStore) => webStore.id === webStoreIdParam)
    ) {
      return hasPermission(RoleActions.ACCESS_DEFAULT_GAS_SHOPS);
    }

    if (
      globalGasWebStores.value.some(
        (webStore) => webStore.id === webStoreIdParam,
      )
    ) {
      return hasPermission(RoleActions.ACCESS_GLOBAL_GAS_SHOP);
    }
  };

  // ------ Case: User without app permission wants to add a new customer ----------------------
  const routesAccessibleWithoutAppPermission: RouteRecordName[] = [
    AppRouteNames.FbsHubAddCustomer,
    AppRouteNames.GasShopAddCustomer,
  ];

  const canAccessRouteWithoutAppPermission = ref(
    routesAccessibleWithoutAppPermission.includes(to.name),
  );

  // ------ Case: User wants to apply for manager role -----------------------------------------
  // If the selected customer is not managed, the user can apply for the account manager role
  const canApplyForAccountManagerRole = computed(
    () => selectedCustomer.value?.isManaged === false && isNotProduction(), // TODO: remove "isNotProduction()" when wid-overview page goes live
  );

  return {
    initializeUserData,
    hasOtherCustomerWithPermission,
    selectCustomerWithPermission,
    hasPermissionForWebStore,
    hasUserData,
    hasFetchingUserError,
    accessRouteWithCustomerRequired,
    needsToCompleteRegistration,
    canAccessRouteWithoutAppPermission,
    canApplyForAccountManagerRole,
  };
};
